import { Box, List } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { getExampleTextListApi } from "../../../api/endpoints/generalAPI";
import QUERY_KEYS from "../../../api/queryKeys";
import { Example } from "./Example";
import styles from "./Example.module.css";
import { ExampleTextEntry } from "../../../api/types/commonTypes";

interface Props {
    onExampleClicked: (value: string) => void;
}

const MotionList = motion(List);

export const ExampleList = ({ onExampleClicked }: Props) => {
    const { t, i18n } = useTranslation("translation", { keyPrefix: "chat" });
    const {
        data: examples,
        status,
        error
    } = useQuery<ExampleTextEntry[]>({
        queryKey: [QUERY_KEYS.EXAMPLE_TEXT_LIST],
        queryFn: getExampleTextListApi,
        staleTime: 1000 * 60 * 60 // 1 hour
    });

    const langKey: keyof ExampleTextEntry = (i18n.language ?? "hu") as keyof ExampleTextEntry;

    return (
        <MotionList
            layout="position"
            className={styles.examplesNavList}
            sx={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                alignItems: "stretch",
                flexWrap: "wrap",
                paddingBlock: "16px",
                width: "100%",
                margin: "0 auto"
            }}
            initial="initial"
            animate="animate"
        >
            {status === "pending" && <Box sx={{ minHeight: "60px" }} />}
            {error && <div>{t("exampleList.fetchingError")}</div>}
            <AnimatePresence>
                {status === "success" &&
                    examples.map((example, i) => {
                        const text = (example?.[langKey] ?? example?.hu ?? example?.en ?? "") as string;
                        return <Example key={example.hu + example.en} text={text} value={text} onClick={onExampleClicked} delay={i} />;
                    })}
            </AnimatePresence>
        </MotionList>
    );
};
